import {
  DestroyRef,
  Directive,
  Input,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RbacService } from '../services/rbac/rbac.service';

@Directive({
  selector: '[appHasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnInit {
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly rbacService = inject(RbacService);
  private readonly destroyRef = inject(DestroyRef);

  private permissions: string[] = [];
  private mode: 'any' | 'all' = 'any';
  private contextLoaded = false;

  @Input()
  set appHasPermission(value: string | readonly string[]) {
    this.permissions = Array.isArray(value) ? [...value] : [value];
    this.updateView();
  }

  @Input()
  set appHasPermissionMode(value: 'any' | 'all') {
    this.mode = value === 'all' ? 'all' : 'any';
    this.updateView();
  }

  ngOnInit(): void {
    if (this.rbacService.isContextReady()) {
      this.contextLoaded = true;
      this.updateView();
    }

    this.rbacService.contextChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.contextLoaded = this.rbacService.isContextReady();
        this.updateView();
      });

    this.rbacService
      .ensureContextLoaded()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.contextLoaded = true;
        this.updateView();
      });
  }

  private updateView(): void {
    if (!this.contextLoaded || !this.permissions.length) {
      this.viewContainer.clear();
      return;
    }

    const allowed =
      this.mode === 'all'
        ? this.permissions.every((p) => this.rbacService.hasPermission(p))
        : this.rbacService.hasAnyPermission(this.permissions);

    this.viewContainer.clear();
    if (allowed) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
  }
}
